/**
 * #6428: motivo da reprovação na análise cadastral.
 *
 * A analista escolhe um motivo principal (obrigatório para finalizar uma
 * reprovação) e, se quiser, outros que também pesaram. A lista vem da tabela
 * motivos_reprovacao_analise, para poder mudar sem publicar o sistema.
 *
 * Não confundir com negociacoes.termo_reprovado_motivo (termoReprovado.ts):
 * aquele diz POR ONDE a reprovação veio (análise, ligação); este diz POR QUÊ.
 *
 * O banco repete as regras daqui (fn_analise_motivo_reprovacao): sem motivo
 * fora de "Reprovado", outros sem repetir o principal e na ordem da lista.
 */

export interface MotivoReprovacao {
  codigo: string;
  rotulo: string;
  descricao: string;
  ordem: number;
  /** "Outro": exige a descrição no parecer (ressalvas_condicoes). */
  exige_descricao: boolean;
  /** Desativado some da escolha, mas continua valendo nas análises antigas. */
  ativo: boolean;
}

export interface DadosMotivoReprovacao {
  decisao_final?: string | null;
  motivo_reprovacao?: string | null;
  motivos_reprovacao_outros?: string[] | null;
  ressalvas_condicoes?: string | null;
}

export const DECISAO_REPROVADO = 'Reprovado';

const porOrdem = (motivos: MotivoReprovacao[]) => {
  const ordem = new Map(motivos.map(m => [m.codigo, m.ordem]));
  return (a: string, b: string) => (ordem.get(a) ?? Number.MAX_SAFE_INTEGER) - (ordem.get(b) ?? Number.MAX_SAFE_INTEGER);
};

/**
 * O que falta para finalizar uma reprovação. Vazio quando não falta nada.
 *
 * Também vazio quando a lista não carregou (tabela ainda não criada, rede):
 * aí não há o que escolher, e travar a reprovação seria pior que seguir sem
 * motivo — o parecer escrito continua obrigatório como sempre foi.
 */
export function pendenciasMotivoReprovacao(
  dados: DadosMotivoReprovacao,
  motivos: MotivoReprovacao[] | null | undefined,
): string[] {
  if (dados.decisao_final !== DECISAO_REPROVADO || !motivos || motivos.length === 0) return [];

  const pendencias: string[] = [];
  if (!dados.motivo_reprovacao) pendencias.push('Escolha o motivo principal da reprovação.');

  const escolhidos = new Set([dados.motivo_reprovacao, ...(dados.motivos_reprovacao_outros ?? [])]);
  const exigeTexto = motivos.find(m => m.exige_descricao && escolhidos.has(m.codigo));
  if (exigeTexto && !dados.ressalvas_condicoes?.trim()) {
    pendencias.push(`Motivo "${exigeTexto.rotulo}": descreva o motivo no campo Ressalvas / Condições.`);
  }
  return pendencias;
}

/** Troca o motivo principal; se ele estava entre os outros, sai de lá. */
export function escolherMotivoPrincipal(codigo: string, outros: string[] | null | undefined) {
  return {
    motivo_reprovacao: codigo,
    motivos_reprovacao_outros: (outros ?? []).filter(c => c !== codigo),
  };
}

/** Marca ou desmarca um motivo extra, mantendo a ordem da lista. O principal nunca entra. */
export function alternarMotivoOutro(
  codigo: string,
  principal: string | null | undefined,
  outros: string[] | null | undefined,
  motivos: MotivoReprovacao[],
): string[] {
  const atuais = (outros ?? []).filter(c => c !== principal);
  const novos = atuais.includes(codigo) ? atuais.filter(c => c !== codigo) : codigo === principal ? atuais : [...atuais, codigo];
  return novos.sort(porOrdem(motivos));
}

/**
 * Campos a gravar junto com uma nova decisão. Fora de "Reprovado" o motivo
 * deixa de existir. Vazio quando não há nada a limpar, para não mandar coluna
 * à toa (o que quebraria o salvamento se a migration ainda não estiver no banco).
 */
export function limparMotivoAoMudarDecisao(
  novaDecisao: string,
  dados: DadosMotivoReprovacao,
): { motivo_reprovacao?: null; motivos_reprovacao_outros?: string[] } {
  if (novaDecisao === DECISAO_REPROVADO) return {};
  const temMotivo = !!dados.motivo_reprovacao || (dados.motivos_reprovacao_outros?.length ?? 0) > 0;
  return temMotivo ? { motivo_reprovacao: null, motivos_reprovacao_outros: [] } : {};
}

/** Rótulo de um código. Código que não está na lista aparece como veio, em vez de sumir. */
export function rotuloMotivoReprovacaoAnalise(codigo: string | null | undefined, motivos: MotivoReprovacao[] | null | undefined): string | null {
  if (!codigo) return null;
  return motivos?.find(m => m.codigo === codigo)?.rotulo ?? codigo;
}

/** Principal + outros em texto, para notificação e listas. `null` quando não há motivo. */
export function descreverMotivosReprovacao(
  dados: DadosMotivoReprovacao,
  motivos: MotivoReprovacao[] | null | undefined,
): { principal: string; outros: string[] } | null {
  const principal = rotuloMotivoReprovacaoAnalise(dados.motivo_reprovacao, motivos);
  if (!principal) return null;
  const outros = (dados.motivos_reprovacao_outros ?? [])
    .filter(c => c && c !== dados.motivo_reprovacao)
    .map(c => rotuloMotivoReprovacaoAnalise(c, motivos)!);
  return { principal, outros };
}
